import {
  Activity,
  ArrowLeft,
  Calendar,
  Clock,
  Info,
  RefreshCw,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LiveSessionBlock, LiveSessionBlockHandle } from '../components/LiveSessionBlock';
import { SessionDataVisualizer } from '../components/SessionDataVisualizer';
import { apiService } from '../services/api';
import { utilService } from '../services/util';

export const SessionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(searchParams.get('refresh') === 'true' ? 1 : 0);
  const [notes, setNotes] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'waiting' | 'saving' | 'success' | 'error'>('idle');
  // Preserve q and page for "Back to Sessions" (passed from list; do not persist elsewhere)
  const backToSessionsUrl = `/sessions${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`;
  const liveBlockRef = useRef<LiveSessionBlockHandle | null>(null);
  const notesRef = useRef<HTMLDivElement | null>(null);
  const hasUserEditedMetadata = useRef(false);
  const dirtyNotesRef = useRef(false);
  const dirtyTagsRef  = useRef(false);

  // Fetch session details
  const { data: sessionData, isLoading: sessionLoading, error: sessionError } = useQuery(
    ['session', id],
    () => apiService.getSession(id!),
    {
      enabled: !!id,
    }
  );

  // Fetch session data for visualization
  // refreshKey > 0 indicates a manual refresh was triggered
  // If session status is 'active', refresh data by default
  const {
    data: sessionDataCompiled,
    isLoading: dataLoading,
    error: dataError,
    refetch: refetchSessionData,
  } = useQuery(
    ['sessionData', id, refreshKey],
    () => apiService.getSessionData(id!, refreshKey > 0 || session?.end === null),
    {
      enabled: !!id,
    }
  );

  const session = sessionData?.session;

  // Extract sessionData from the API response
  // apiService.getSessionData returns: { sessionData: SessionDataCompiled }
  // So sessionDataCompiled = { sessionData: SessionDataCompiled }
  // Therefore: compiledData = sessionDataCompiled.sessionData
  const compiledData = sessionDataCompiled;

  // Memoize so LiveSessionBlock's websocket effect doesn't reconnect while typing Notes/Tags.
  const handleLiveComplete = useCallback(() => {
    setRefreshKey(prev => prev + 1);
    refetchSessionData();
  }, [refetchSessionData]);

  // Initialize notes and tags from session metadata when session loads
  useEffect(() => {
    if (!session) return;
    const meta = session.metadata as { notes?: string; tags?: string[] } | undefined;
    if (meta?.notes) {
      const sanitized = utilService.sanitizeHtml(meta.notes);
      setNotes(sanitized);
      if (notesRef.current) {
        notesRef.current.innerHTML = sanitized;
      }
    }
    if (meta?.tags && Array.isArray(meta.tags)) {
      setTags(meta.tags);
    }
  }, [session]);

  // Debounced save of notes/tags to API (3s after last change)
  useEffect(() => {
    if (!id) return;
    // Only send updates after the user has edited notes or tags (not on initial load)
    if (!hasUserEditedMetadata.current) return;
    // Do not send if nothing is marked dirty
    if (!dirtyNotesRef.current && !dirtyTagsRef.current) return;

    setSaveStatus('waiting');
    let handle = window.setTimeout(() => {
      (async () => {
        setSaveStatus('saving');
        try {
          const metadata: { notes?: string | null; tags?: string[] | null } = {};

          if (dirtyNotesRef.current) {
            if ( notes ) {
              let sanitized = utilService.sanitizeHtml(notes);
              if ( sanitized && (notesRef.current?.innerText.trim().length ?? 0) > 0 ) {
                metadata.notes = sanitized;
              } else {
                metadata.notes = null;
              }
            } else {
              metadata.notes = null;
            }
          }

          if (dirtyTagsRef.current) {
            metadata.tags = tags.length > 0 ? tags : null;
          }

          if (Object.keys(metadata).length === 0) {
            setSaveStatus('idle');
            return;
          }

          await apiService.updateSessionMetadata(id, { metadata });

          dirtyNotesRef.current = false;
          dirtyTagsRef.current  = false;

          setSaveStatus('success');
          handle = window.setTimeout(() => { setSaveStatus('idle'); }, 3000);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update session metadata', error);
          setSaveStatus('error');
        }
      })();
    }, 3000);

    return () => window.clearTimeout(handle);
  }, [id, notes, tags]);

  if (sessionLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-secondary-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (sessionError || !session) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate(backToSessionsUrl)}
          className="flex items-center space-x-2 text-secondary-600 hover:text-secondary-900"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Sessions</span>
        </button>
        <div className="card text-center py-12">
          <Activity className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
          <p className="text-secondary-500 mb-2">Failed to load session</p>
          <p className="text-sm text-secondary-400">The session may not exist or you may not have permission to view it.</p>
        </div>
      </div>
    );
  }
  // const sessionAge = new Date().getTime() - new Date(session.start).getTime();
  const sessionStatus = utilService.sessionStatus(session);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(backToSessionsUrl)}
            className="flex items-center space-x-2 text-secondary-600 hover:text-secondary-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Sessions</span>
          </button>
        </div>
        {sessionStatus !== 'Live' && (
        <button
          onClick={() => {
            // Increment refreshKey to trigger a new query with refresh=true
            setRefreshKey(prev => prev + 1);
            refetchSessionData();
          }}
          className="btn-secondary flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        )}
      </div>

      {/* Session Info Card */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Activity className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">
              Session Details: {utilService.sessionName(session)}
              {session.metadata && session.metadata.sessionNumber && session.metadata.totalSessions && (
                <span className="font-mono font-thin text-secondary-400 ml-2">[{session.metadata.sessionNumber}/{session.metadata.totalSessions}]</span>
              )}
            </h1>
            <p className="text-sm text-secondary-500">Session ID: {utilService.extractId(session)}</p>
            {session.userId && typeof session.userId === 'object' && (
              <p className="text-sm text-secondary-500">{(session.userId as any).fullName}</p>
            )}
            {session.name && session.sessionDefinitionId && typeof session.sessionDefinitionId === 'object' && (
              <p className="text-sm text-secondary-500">{(session.sessionDefinitionId as any).name}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-secondary-100 rounded-lg">
              <Calendar className="h-5 w-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-secondary-600">Start Time</p>
              <p className="text-base text-secondary-900">{utilService.formatDateTime(session.start)}</p>
            </div>
          </div>

          {session.end && (
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-secondary-100 rounded-lg">
                <Calendar className="h-5 w-5 text-secondary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-600">End Time</p>
                <p className="text-base text-secondary-900">{utilService.formatDateTime(session.end)}</p>
              </div>
            </div>
          )}

          <div className="flex items-start space-x-3">
            <div className="p-2 bg-secondary-100 rounded-lg">
              <Clock className="h-5 w-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-secondary-600">Duration</p>
              <p className="text-base text-secondary-900">{session.end ? utilService.formatDuration(session.start, session.end) : '---'}</p>
            </div>
          </div>

          {session.programId && (
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-secondary-100 rounded-lg">
                <Info className="h-5 w-5 text-secondary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-600">Program</p>
                <p className="text-base text-secondary-900 font-mono text-sm">
                  {utilService.sessionProgramName(session, true)}
                </p>
              </div>
            </div>
          )}

          {session.providerId && (
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-secondary-100 rounded-lg">
                <Info className="h-5 w-5 text-secondary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-600">Provider</p>
                <p className="text-base text-secondary-900 font-mono text-sm">
                  {typeof session.providerId === 'object' && (session.providerId as any).name ? (
                    (session.providerId as any).name
                  ) : (
                    utilService.extractId(session.providerId as any)
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start space-x-3">
            <div className="p-2 bg-secondary-100 rounded-lg">
              <Info className="h-5 w-5 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-secondary-600">Content</p>
              {session.detailedContent?.software && session.detailedContent?.software?.length > 0 && (
                <ul className="list-disc list-inside">Software:
                  {session.detailedContent?.software?.map((s) => (
                    <li key={utilService.extractId(s)} className="text-sm text-secondary-500 nowrap">{s.name} by {s.publisher}{s.ver ? ` (${s.ver})` : ''}</li>
                  ))}
                </ul>
              )}
              {session.detailedContent?.hardware && session.detailedContent?.hardware?.length > 0 && (
                <ul className="list-disc list-inside">Hardware:
                  {session.detailedContent?.hardware?.map((h) => (
                    <li key={utilService.extractId(h)} className="text-sm text-secondary-500 nowrap">{h.make} ({h.hardwareModel}{h.revision ? ' ' + h.revision : ''})</li>
                  ))}
                </ul>
              )}
              {/* Services, when they become available */}
              {session.detailedContent?.services && session.detailedContent?.services?.length > 0 && (
                <ul className="list-disc list-inside">Services:
                  {session.detailedContent?.services?.map((s) => (
                    <li key={utilService.extractId(s)} className="text-sm text-secondary-500 nowrap">{s.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-secondary-200">
          <div className="flex items-center space-x-2">
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full ${utilService.sessionStatusClass(session)}`}
            >
              {sessionStatus}
            </span>
          </div>
        </div>
      </div>

      {/* Session Data Visualization */}
      <div className="card">
        {sessionStatus === 'Live' && id && session ? (
          <LiveSessionBlock
            ref={liveBlockRef}
            sessionId={id}
            sessionStart={session.start}
            onComplete={handleLiveComplete}
          />
        ) : dataLoading ? (
          <div className="space-y-4">
            <div className="animate-pulse">
              <div className="h-6 bg-secondary-200 rounded w-1/3 mb-4"></div>
              <div className="h-96 bg-secondary-200 rounded"></div>
            </div>
          </div>
        ) : dataError ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500 mb-2">Failed to load session data</p>
            <p className="text-sm text-secondary-400">
              The session data may not be available yet or there was an error loading it.
            </p>
          </div>
        ) : compiledData ? (
          <SessionDataVisualizer sessionData={compiledData} />
        ) : (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No session data available</p>
            <p className="text-sm text-secondary-400">
              Session data will appear here once it's been collected.
            </p>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-secondary-900">Notes</h2>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          {/* Rich-text notes area (left 2/3) */}
          <div className="md:w-2/3 w-full">
            <div className="border border-secondary-200 rounded-lg bg-white">
              <div className="flex items-center justify-between px-3 py-2 border-b border-secondary-200 bg-secondary-50">
                <span className="text-xs font-medium text-secondary-500">Session Notes</span>
                <span className="text-[10px] text-secondary-400">Rich text</span>
              </div>
              <div
                className="min-h-[160px] max-h-[360px] overflow-auto px-3 py-2 text-sm text-secondary-900 focus:outline-none"
                contentEditable
                suppressContentEditableWarning
                ref={notesRef}
                onPaste={e => {
                  e.preventDefault();
                  const clipboardData = e.clipboardData || (window as any).clipboardData;
                  const pastedHtml = clipboardData.getData('text/html');
                  const pastedText = clipboardData.getData('text/plain');
                  const raw = pastedHtml || pastedText || '';
                  const sanitized = utilService.sanitizeHtml(raw);
                  if (notesRef.current) {
                    // Insert sanitized HTML at the current caret position
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                      const range = selection.getRangeAt(0);
                      range.deleteContents();
                      const temp = document.createElement('div');
                      temp.innerHTML = sanitized;
                      const fragment = document.createDocumentFragment();
                      while (temp.firstChild) {
                        fragment.appendChild(temp.firstChild);
                      }
                      range.insertNode(fragment);
                      // Move caret to the end of the inserted content
                      selection.collapseToEnd();
                    } else {
                      notesRef.current.innerHTML = sanitized;
                    }
                  }
                  hasUserEditedMetadata.current = true;
                  setNotes(prev => utilService.sanitizeHtml((notesRef.current?.innerHTML ?? '') || sanitized));
                }}
                onInput={e => {
                  const target = e.target as HTMLDivElement;
                  const rawHtml = target.innerHTML;
                  const sanitized = utilService.sanitizeHtml(rawHtml);
                  hasUserEditedMetadata.current = true;
                  dirtyNotesRef.current = true;
                  setNotes(sanitized);
                }}
              />
            </div>
          </div>

          {/* Tags (right 1/3) */}
          <div className="md:w-1/3 w-full">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-secondary-800">Tags</h3>
            </div>
              <div className="mb-3 flex flex-wrap gap-2">
              {tags.length === 0 && (
                <p className="text-xs text-secondary-400">No tags yet. Add your first tag below.</p>
              )}
              {tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 border border-primary-100"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      hasUserEditedMetadata.current = true;
                      dirtyTagsRef.current = true;
                      setTags(prev => prev.filter(t => t !== tag));
                    }}
                    className="p-0.5 rounded-full hover:bg-primary-100 focus:outline-none focus:ring-1 focus:ring-primary-300"
                    aria-label={`Remove tag ${tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <form
              onSubmit={e => {
                e.preventDefault();
                const trimmed = newTag.trim();
                if (!trimmed) return;
                hasUserEditedMetadata.current = true;
                dirtyTagsRef.current = true;
                setTags(prev => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
                setNewTag('');
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                className="flex-1 rounded-md border border-secondary-200 bg-white px-2.5 py-1.5 text-xs text-secondary-900 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                placeholder="Add a new tag"
              />
              <button
                type="submit"
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                Add
              </button>
            </form>
          </div>
        </div>
        <div className="mt-3 flex justify-center">
            {saveStatus === 'idle' && (
              <div className="inline-flex items-center gap-2 text-xs text-secondary-500 height-4"/>
            )}
            {saveStatus === 'waiting' && (
              <div className="inline-flex items-center gap-2 text-xs text-secondary-500">
                <span>Listening for changes...</span>
              </div>
            )}
            {saveStatus === 'saving' && (
              <div className="inline-flex items-center gap-2 text-xs text-secondary-500">
                <span className="inline-block h-3 w-3 border-2 border-secondary-300 border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'success' && (
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-green-100 text-xs font-medium text-green-800">
                All changes saved.
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-100 text-xs font-medium text-red-800">
                Changes could not be saved
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
